import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useCohortCancellation, useCohortRetention } from '@/lib/api/adminHooks';
import type { CohortCycleFilter } from '@/types/admin';

import { CohortHeatmap, type HeatmapMetric } from './CohortHeatmap';
import { SyncStatusBar } from './SyncStatusBar';

const DEFAULT_CAP: Record<HeatmapMetric, number> = {
  retention: 6,
  cancellation: 4,
};

const CAP_OPTIONS: Record<HeatmapMetric, number[]> = {
  retention: [3, 4, 5, 6, 8, 12],
  cancellation: [3, 4, 6, 8],
};

/**
 * Product-label drill-down. Shows every CP product_id that shares the same
 * normalized label as individual cohort tables — useful when the same
 * treatment exists under multiple IDs (experiment clones, canonical-plus-
 * copy duplicates, test products).
 *
 * `?metric=cancellation` opens the view in cancellation mode (linked from
 * the cohort index when drilling in from the cancellation heatmap).
 */
export function ProductDetailScreen() {
  const { label: labelParam } = useParams<{ label: string }>();
  const label = labelParam ? decodeURIComponent(labelParam) : '';
  const [searchParams] = useSearchParams();
  const initialMetric: HeatmapMetric =
    searchParams.get('metric') === 'cancellation' ? 'cancellation' : 'retention';

  const [metric, setMetric] = useState<HeatmapMetric>(initialMetric);
  const [cycle, setCycle] = useState<CohortCycleFilter>('monthly');
  const [days, setDays] = useState(180);
  const [cap, setCap] = useState(DEFAULT_CAP[initialMetric]);

  const retention = useCohortRetention({
    cycle,
    days,
    cap,
    productLabel: label,
    groupBy: 'product_id',
    enabled: metric === 'retention',
  });
  const cancellation = useCohortCancellation({
    cycle,
    days,
    cap,
    productLabel: label,
    groupBy: 'product_id',
    enabled: metric === 'cancellation',
  });
  const { data, isLoading, isError, error } =
    metric === 'retention' ? retention : cancellation;

  const handleMetricChange = (next: HeatmapMetric) => {
    setMetric(next);
    setCap(DEFAULT_CAP[next]);
  };

  const isCancellation = metric === 'cancellation';
  const capUnitLabel = isCancellation ? 'Weeks' : 'Months';
  const capPrefix = isCancellation ? 'W' : 'M';

  const variantCount = data?.tables.length ?? 0;
  const totalSubs = data?.tables.reduce((acc, t) => acc + t.n_total, 0) ?? 0;

  return (
    <div className="flex flex-col gap-24">
      <div>
        <Link
          to="/admin/cohorts"
          className="flex items-center gap-6 type-label-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to cohorts
        </Link>
      </div>

      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">{label || 'Product'}</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          {variantCount > 0
            ? `${variantCount} CP product variant${variantCount === 1 ? '' : 's'} share this label · ${totalSubs.toLocaleString()} subs total`
            : 'Loading variants…'}
        </p>
      </div>

      <SyncStatusBar />

      <div className="flex flex-wrap items-center gap-16 p-12 border border-line rounded-lg">
        <Segmented
          label="Metric"
          options={[
            { label: 'Retention', value: 'retention' },
            { label: 'Early cancellation', value: 'cancellation' },
          ]}
          value={metric}
          onChange={(v) => handleMetricChange(v as HeatmapMetric)}
        />
        <Segmented
          label="Cycle"
          options={[
            { label: 'Monthly', value: 'monthly' },
            { label: 'Quarterly', value: 'quarterly' },
            { label: 'All', value: 'all' },
          ]}
          value={cycle}
          onChange={(v) => setCycle(v as CohortCycleFilter)}
        />
        <label className="flex items-center gap-8 type-label-sm">
          <span className="text-ink-muted">Window</span>
          <select
            className="bg-raised border border-line rounded px-8 py-4"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
            <option value={730}>2 years</option>
          </select>
        </label>
        <label className="flex items-center gap-8 type-label-sm">
          <span className="text-ink-muted">{capUnitLabel}</span>
          <select
            className="bg-raised border border-line rounded px-8 py-4"
            value={cap}
            onChange={(e) => setCap(Number(e.target.value))}
          >
            {CAP_OPTIONS[metric].map((n) => (
              <option key={n} value={n}>
                {capPrefix}0–{capPrefix}
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isError && (
        <div className="p-16 border border-line rounded-lg bg-raised">
          <p className="type-label-md text-ink">Failed to load product data.</p>
          <p className="type-label-sm text-ink-muted mt-4">{String(error)}</p>
        </div>
      )}

      {isLoading && !data && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          Loading…
        </div>
      )}

      {data && data.tables.length === 0 && (
        <div className="p-16 border border-line rounded-lg bg-raised type-label-md text-ink-muted">
          No CP products match this label in the selected window.
        </div>
      )}

      <div className="flex flex-col gap-20">
        {data?.tables.map((table) => (
          <CohortHeatmap key={table.product_id} table={table} metric={metric} />
        ))}
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-8 type-label-sm">
      <span className="text-ink-muted">{label}</span>
      <div className="flex rounded border border-line overflow-hidden">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-12 py-6 ${
              value === o.value
                ? 'bg-ink text-ink-onAccent'
                : 'bg-raised hover:bg-sunken text-ink-muted'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
